import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useLocation } from 'react-router';
import {
    useNavigate,
    Link
} from 'react-router-dom';

// material-ui
import {
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
    TextField
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

import AnimateButton from 'components/@extended/AnimateButton';

// assets
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import AuthService from 'services/auth/Auth';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'routes/const';

// ============================|| FIREBASE - LOGIN ||============================ //

const AuthLogin = () => {

    const { path } = useLocation();

    const { t, i18n } = useTranslation();

    const [showPassword, setShowPassword] = React.useState(false);

    const navigate = useNavigate();


    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleValidationMessage = (err) => {
        const validationMessages = {
            invalidUsernameOrPassword: t('views.login.validation.invalid_username_or_password'),
        }
        if (err.response?.data?.msgKey) {
            if (err.response?.data?.msgKey in validationMessages)
                return validationMessages[err.response?.data?.msgKey];
            return err.response?.data?.msgKey
        }
        return err.message;
    }

    return (
        <>
            <Formik
                initialValues={{
                    email: '',
                    password: '',
                    submit: null
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string().email("Should be correct email address").required('Email is required'),
                    password: Yup.string().min(5).max(255).required('Password is required')
                })}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    try {
                        await AuthService.login(values);
                        setStatus({ success: false });
                        setSubmitting(false);
                        location.reload();
                    } catch (err) {
                        const key = err.response.data?.data?.key;
                        setStatus({ success: false });
                        setErrors({ submit: handleValidationMessage(err) });
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, isValid, dirty }) => (
                    <form noValidate onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <TextField
                                        id="email-login"
                                        type="text"
                                        label={t("views.login.email")}
                                        value={values.email}
                                        name="email"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        placeholder="Enter email"
                                        fullWidth
                                        error={Boolean(touched.email && errors.email)}
                                    />
                                    {touched.email && errors.email && (
                                        <FormHelperText error id="standard-weight-helper-text-username-login">
                                            {errors.email}
                                        </FormHelperText>
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack spacing={1}>

                                    <TextField
                                        fullWidth
                                        error={Boolean(touched.password && errors.password)}
                                        id="-password-login"
                                        type={showPassword ? 'text' : 'password'}
                                        value={values.password}
                                        name="password"
                                        label={t("views.login.password")}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        InputProps={{
                                            endAdornment:
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        onMouseDown={handleMouseDownPassword}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                    </IconButton>
                                                </InputAdornment>
                                        }
                                        }
                                        placeholder="Enter password"
                                    />
                                    {touched.password && errors.password && (
                                        <FormHelperText error id="standard-weight-helper-text-password-login">
                                            {errors.password}
                                        </FormHelperText>
                                    )}
                                </Stack>
                            </Grid>
                            {errors.submit && (
                                <Grid item xs={12}>
                                    <FormHelperText error>{errors.submit}</FormHelperText>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography sx={{ color: "#151F57", fontWeight: 600, textDecoration: "none" }} component={Link} to={ROUTES.FORGOT_PASSWORD}>Forgot password?</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <AnimateButton>
                                    <Button
                                        disableElevation
                                        disabled={isSubmitting || !isValid || !dirty}
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                        sx={{ backgroundColor: "#151F57", fontSize: "18px", textTransform: "initial" }}
                                    >
                                        {t("views.login.login")}
                                    </Button>
                                </AnimateButton>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Formik>
        </>
    );
};

export default AuthLogin;
